import { createSlice } from '@reduxjs/toolkit'
import {
  takePlo5SeatThunk,
  takePlo5ActionThunk,
  updatePlo5PlayerSittingStatusThunk,
  updatePlo5BuyInAmountThunk,
  changePlo5SeatThunk,
  getPlo5GameTablesThunk
} from 'redux-thunk/thunk/Plo5GameManagement.thunk'

const initialState = {
  gameData: null,
  gameState: 'idle',
  Plo5GameTables: [],
  currentSeat: null,
  playerActions: [],
  gamePot: 0,
  communityCards: [],
  currentPlayer: null,
  loading: false,
  // Specific loading states for different actions
  takingSeatLoading: false,
  takingActionLoading: false,
  error: null,
  pixi: {
    isAssetsLoaded: false,
    isInitialized: false
  }
}

const Plo5Slice = createSlice({
  name: 'plo5',
  initialState,
  reducers: {
    setGameData: (state, action) => {
      state.gameData = action.payload
    },
    setGameState: (state, action) => {
      state.gameState = action.payload
    },
    setCurrentSeat: (state, action) => {
      state.currentSeat = action.payload
    },
    addPlayerAction: (state, action) => {
      state.playerActions.push(action.payload)
    },
    setGamePot: (state, action) => {
      state.gamePot = action.payload
    },
    setCommunityCards: (state, action) => {
      state.communityCards = action.payload
    },
    setCurrentPlayer: (state, action) => {
      state.currentPlayer = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setPixiAssetsLoaded: (state, action) => {
      state.pixi.isAssetsLoaded = action.payload
    },
    setPixiInitialized: (state, action) => {
      state.pixi.isInitialized = action.payload
    },
    updateTableSeatOccupied: (state, action) => {
      const { tableId, seatsOccupied } = action.payload
      const tableIndex = state.Plo5GameTables.findIndex(table => String(table.id) === String(tableId))
      if (tableIndex !== -1) {
        state.Plo5GameTables[tableIndex].seatsOccupied = seatsOccupied
      }
    }
  },
  extraReducers: (builder) => {
    // Take Plo5 Seat
    builder
      .addCase(takePlo5SeatThunk.pending, (state) => {
        state.takingSeatLoading = true
        state.error = null
      })
      .addCase(takePlo5SeatThunk.fulfilled, (state, action) => {
        state.takingSeatLoading = false
        state.currentSeat = action.payload
      })
      .addCase(takePlo5SeatThunk.rejected, (state, action) => {
        state.takingSeatLoading = false
        state.error = action.error.message
      })

    // Take Plo5 Action
    builder
      .addCase(takePlo5ActionThunk.pending, (state) => {
        state.takingActionLoading = true
        state.error = null
      })
      .addCase(takePlo5ActionThunk.fulfilled, (state, action) => {
        state.takingActionLoading = false
        state.playerActions.push(action.payload)
        // Update pot if action affects it
        if (action.payload.potAmount) {
          state.gamePot = action.payload.potAmount
        }
      })
      .addCase(takePlo5ActionThunk.rejected, (state, action) => {
        state.takingActionLoading = false
        state.error = action.error.message
      })

    // Update Plo5 Player Sitting Status
    builder
      .addCase(updatePlo5PlayerSittingStatusThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlo5PlayerSittingStatusThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new sitting status
        if (state.gameData) {
          state.gameData.playerSittingStatus = action.payload
        }
      })
      .addCase(updatePlo5PlayerSittingStatusThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Update Plo5 Buy In Amount
    builder
      .addCase(updatePlo5BuyInAmountThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlo5BuyInAmountThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new buy-in amount
        if (state.gameData) {
          state.gameData.buyInAmount = action.payload
        }
      })
      .addCase(updatePlo5BuyInAmountThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Change Plo5 Seat
    builder
      .addCase(changePlo5SeatThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(changePlo5SeatThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentSeat = action.payload
      })
      .addCase(changePlo5SeatThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Get Plo5 Game Tables
    builder
      .addCase(getPlo5GameTablesThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getPlo5GameTablesThunk.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload?.data ?? action.payload
        // Backend returns { tables: [...], pagination: {...} }
        const tables = Array.isArray(data) ? data : (data?.tables ?? data?.rows ?? [])
        state.Plo5GameTables = tables.map(table => ({
          ...table,
          seatsOccupied: table.seatsOccupied !== undefined
            ? table.seatsOccupied
            : table?.tableSeats?.filter(seat => seat.userId !== null && seat.sittingOut === false).length || 0
        }))
      })
      .addCase(getPlo5GameTablesThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})

export const {
  setGameData,
  setGameState,
  setCurrentSeat,
  addPlayerAction,
  setGamePot,
  setCommunityCards,
  setCurrentPlayer,
  clearError,
  updateTableSeatOccupied,

  // pixi
  setPixiAssetsLoaded,
  setPixiInitialized
} = Plo5Slice.actions

export default Plo5Slice.reducer
